/*
 * RegCategoryField
 */

// BASE
import React, { useState, useMemo, useCallback } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import PropTypes from 'prop-types';

// CUSTOM COMPONENTS
import QCheckbox from 'components/QCheckbox';
import CategoryField from 'components/QuickenControls/CategoryField';
import QTypography from 'components/MUIWrappers/QTypography';
import QTip from 'components/QuickenControls/QTip';

// SELECTORS, ACTIONS, UTILS
import { isSplitTxn } from 'data/transactions/utils';
import { CategoryTypeEnum } from 'data/categories/types';
import { isAcme } from 'isAcme';
import { getFeatureFlags } from 'utils/featureFlags/selectors';
import { isOldEdge } from 'utils/utils';

// MUI COMPONENTS
import Typography from '@material-ui/core/Typography';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

// PATH RELATIVE IMPORTS
import { styles } from './styles';

const isIe = require('is-iexplorer') || isOldEdge();  // eslint-disable-line
const categoryFieldInputProps = {
  style: {
    height: isIe ? 27 : 24,
  },
};

const propDefaults = {
  defaultPropogateStatus: false,
};
const propTypes = {
  defaultPropogateStatus: PropTypes.bool,
  txn: PropTypes.object,
  wrapText: PropTypes.bool,
  editable: PropTypes.bool,
  forceStatic: PropTypes.bool,
  longCats: PropTypes.bool,
  showSplitWindow: PropTypes.bool,
  inputRef: PropTypes.func,
  onChange: PropTypes.func,
};

// STYLES HOOK CREATION
const useStyles = makeStyles(styles);

const RegCategoryField = (props) => {

  // PROPS ============================================================================
  const { txn, wrapText, editable, forceStatic, longCats, showSplitWindow,
    defaultPropogateStatus, inputRef, onChange } = props;
  const theme = useTheme();

  // STATE ============================================================================
  const [propogateStatus, setPropogateStatus] = useState(defaultPropogateStatus);

  // SELECTORS ==========================================================================
  const showCategorySettings = useSelector((state) => getFeatureFlags(state).get('showCategorySettings'), shallowEqual);
  const categoryPropogationEnabled = useSelector((state) => getFeatureFlags(state).get('categoryPropogationEnabled'), shallowEqual);

  // EFFECTS ============================================================================

  // STYLES ============================================================================
  const classes = useStyles(props);

  // INTERNAL FUNCTIONS

  // RENDER ============================================================================

  const categoryFieldHeader = useMemo(() => (<CategoryMenuHeader
    checked={propogateStatus}
    boxClick={() => setPropogateStatus(!propogateStatus)}
    classes={classes}
    payee={txn.payee}
  />), [txn.payee, classes, propogateStatus]);
  const categoryFieldMenuClose = useCallback(() => setPropogateStatus(false), []);
  const categoryFieldOnChange = useCallback((coa) => {
    const e = { target: { value: coa, doPropogate: propogateStatus } };
    onChange(e, 'category');
  }, [propogateStatus, onChange]);

  if (!isSplitTxn(txn)) {
    return (
      <CategoryField
        header={categoryPropogationEnabled && txn.payee?.length > 0 ? categoryFieldHeader : undefined}
        menuClose={categoryFieldMenuClose}
        onChange={categoryFieldOnChange}
        disableUnderline={theme.components.register.noUnderline}
        name={editable ? 'regCatEditable' : 'static'}
        returnSelectionMethod
        editable={editable}
        createEnabled={showCategorySettings}
        recommendedCategoryType={txn.amount > 0 ? CategoryTypeEnum.INCOME : CategoryTypeEnum.EXPENSE}
        clickable={!forceStatic && !showSplitWindow}
        value={txn.coa}
        fontSize={theme.components.register.fontSize.default}
        classNameWhenNotEditable={classes.regItemCore}
        autoFocus
        tooltip={!wrapText}
        longCats={longCats}
        inputRef={inputRef}
        InputProps={categoryFieldInputProps}
      />
    );
  }
  return (
    <QTypography
      clickable
      thinFont
      className={classes.regItemCore}
    >
      --- Split ---
    </QTypography>
  );
};

RegCategoryField.propTypes = propTypes;
RegCategoryField.defaultProps = propDefaults;

export default (RegCategoryField);


/*
 CATEGORY MENU HEADER - this is the addition to the category field
 */

const CategoryMenuHeader = (props) => {

  const { classes, boxClick, checked, payee } = props;

  return (
    <div className={classes.headerContainer} >
      <div style={{ display: 'flex'}} >
        <QCheckbox
          onClick={boxClick}
          checked={checked}
          classes={{ boxBorder: classes.headerCheckbox }}
        />
        <Typography className={classes.headerLabel} variant="body1">
          Apply changes to similar transactions.

          <QTip
            title={`${isAcme ? 'Simplifi' : 'Quicken'} will look forwards and backwards in ` +
              `your transaction list and update all transactions with the payee name "${payee}" to use the ` +
              'selected category.  Additionally, this "rule" will be applied going forward to downloaded transactions'}
          >
            <HelpOutlineIcon
              className={classes.helpIcon}
            />
          </QTip>
        </Typography>
      </div>
    </div>
  );
};

CategoryMenuHeader.propTypes = {
  checked: PropTypes.bool,
  boxClick: PropTypes.func,
  classes: PropTypes.object,
  payee: PropTypes.string,
};
